import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import StaticSeo from '../components/StaticSeo.jsx';

// ─────────────────────────────────────────────────────────────────────────
// THE TINT & COATING IDENTIFIER
// Window Washing Guide — Tool 06 of 09
// ─────────────────────────────────────────────────────────────────────────
//
// Stepwise visual quiz to identify residential glass before applying any
// cleaning method that could damage it. Output: a clear verdict on the
// glass type, with specific guidance on what cleaners are safe and which
// are dangerous.
//
// Identification flow (decision tree):
//   1. Era — when was the home built?
//   2. Visual tint check
//   3. Double-image (multi-pane) test
//   4. Edge inspection (laminated detection)
//   5. Verdict + cleaning protocol
//
// Five possible glass types (in order of damage sensitivity):
//   • Standard single-pane (uncoated, oldest, most forgiving)
//   • Standard double-pane (uncoated, common pre-2010)
//   • Tinted (factory tint, integrated into glass)
//   • Aftermarket film-tinted (vulnerable to ammonia + razor)
//   • Low-E coated (most modern, MOST sensitive to acid)
//   • Laminated (impact glass, common in coastal areas)
//
// Sources: Cardinal Glass care guidelines, Pilkington technical literature,
// IGCC (Insulating Glass Certification Council) installation database.
//
// ─────────────────────────────────────────────────────────────────────────

const QUESTIONS = [
  {
    id: 'era',
    title: 'When was the home built?',
    italic: 'or last had its windows replaced',
    note: 'Window technology changed dramatically over the last fifty years. Era is the single biggest predictor of what coating you are dealing with.',
    options: [
      { id: 'pre1990', label: 'Before 1990', desc: 'Original windows likely single-pane, uncoated', visual: 'older' },
      { id: '1990-2010', label: '1990 — 2010', desc: 'Typically double-pane, possibly low-E', visual: 'middle' },
      { id: 'post2010', label: '2010 or later', desc: 'Almost certainly low-E coated, double or triple pane', visual: 'modern' },
      { id: 'unknown', label: "I don't know", desc: "We'll figure it out from the other tests", visual: 'unknown' },
    ],
  },
  {
    id: 'tint',
    title: 'Is the glass visibly tinted?',
    italic: 'looking from outside in daylight',
    note: 'Stand outside the window in daylight. If the glass looks darker, gray, bronze, or has a noticeable color cast — even a faint one — it is tinted. If it looks like clear glass, it is not.',
    options: [
      { id: 'no', label: 'No — looks like clear glass', desc: 'Standard transparent appearance', visual: 'clear' },
      { id: 'subtle', label: 'Faint cast (silver, blue, or green)', desc: 'Likely a low-E coating, not a tint', visual: 'lowe' },
      { id: 'obvious', label: 'Clearly darker or bronze', desc: 'Factory tint or aftermarket film', visual: 'tinted' },
      { id: 'mirror', label: 'Mirror-like / reflective', desc: 'Reflective coating (one-way glass)', visual: 'reflective' },
    ],
  },
  {
    id: 'panes',
    title: 'How many panes — the double-image test',
    italic: 'hold a small flame or LED light against the glass at night',
    note: 'At night, hold a small candle, lighter, or LED keychain light against one side of the glass. Count the reflections you can see in the glass. One reflection means single-pane. Two means double-pane. Three means triple-pane.',
    options: [
      { id: 'single', label: 'One reflection — single pane', desc: 'Older or single-glazed window', visual: 'single' },
      { id: 'double', label: 'Two reflections — double pane', desc: 'Most common modern installation', visual: 'double' },
      { id: 'triple', label: 'Three reflections — triple pane', desc: 'High-end energy-efficient or commercial', visual: 'triple' },
      { id: 'unsure', label: 'Cannot tell / cannot test', desc: 'Skip this — we will assume modern default', visual: 'unknown' },
    ],
  },
  {
    id: 'edge',
    title: 'Examine the edge of the glass',
    italic: 'where it meets the frame',
    note: "Look at the edge of the glass through the frame, especially where the rubber seal is visible. If you can see a thin plastic interlayer sandwiched between two layers of glass — typically a frosted or yellowish band a few millimeters thick — the glass is laminated. Common in coastal areas (impact glass) and skylights.",
    options: [
      { id: 'plain', label: 'Just glass, sealed at edge', desc: 'Standard tempered or annealed glass', visual: 'plain' },
      { id: 'interlayer', label: 'Visible plastic interlayer', desc: 'Laminated impact-resistant glass', visual: 'laminated' },
      { id: 'sealed', label: 'Sealed under metal frame, cannot see', desc: 'Common — we will work without this data', visual: 'unknown' },
    ],
  },
];

// ─── verdict logic ────────────────────────────────────────────────────────
// Combines answers into a primary glass-type identification.
function diagnose(answers) {
  const { era, tint, panes, edge } = answers;

  // Laminated is determinative — overrides everything else for cleaning purposes
  if (edge === 'interlayer') {
    return 'laminated';
  }

  // Reflective / mirror tint is its own category
  if (tint === 'mirror') {
    return 'reflective';
  }

  // Aftermarket film vs. factory tint distinction
  // Films appear bronze/dark on older windows; factory tint on new ones is more uniform
  if (tint === 'obvious') {
    if (era === 'pre1990' || era === '1990-2010') {
      return 'film-tinted';
    }
    return 'factory-tinted';
  }

  // Low-E indicators: faint cast + modern era + multiple panes
  if (tint === 'subtle' || (era === 'post2010' && panes !== 'single')) {
    return 'low-e';
  }

  // Single-pane uncoated — the oldest and simplest
  if (panes === 'single' && tint === 'no') {
    return 'single-uncoated';
  }

  // Default: standard double-pane uncoated
  return 'double-uncoated';
}

// ─── verdict library ──────────────────────────────────────────────────────
const VERDICTS = {
  'single-uncoated': {
    name: 'Standard Single-Pane',
    italic: 'uncoated, the oldest and most forgiving',
    rating: 'Low sensitivity',
    summary:
      'Plain glass with no coating, no laminate, no tint. Common in homes built before 1990 and in some outbuildings, garages, and storm windows. Forgiving of almost any cleaning method that does not involve abrasives.',
    safe: [
      ['House Standard (Dawn + distilled)', 'The canonical recipe. Always safe.'],
      ['Vinegar 1:1', 'Safe and effective for mineral haze.'],
      ['Windex Original', 'Manufacturer dilution is fine.'],
      ['Razor scrape', 'Safe at proper low angle (25–30°).'],
    ],
    avoid: [
      ['Magic Eraser', 'Will fine-haze the surface from melamine abrasion.'],
      ['Abrasive scrubbing pads', 'Same issue.'],
    ],
    notes:
      'The most commonly damaged single-pane windows are those with putty or oil-based glazing compound at the edges. Avoid letting any solution dwell on the glazing for more than a minute, and never spray ammonia products at the seal — it dries the putty and causes it to crack within a season.',
  },
  'double-uncoated': {
    name: 'Standard Double-Pane',
    italic: 'uncoated, the residential workhorse',
    rating: 'Low-medium sensitivity',
    summary:
      'Two panes of glass with an air or argon gap between them, but no coating on either pane. Common in most residential construction from roughly 1980 to 2005. Treat it like single-pane glass on the outside; just be aware that the seal between the panes is fragile and water inside the gap means a failed seal that cannot be fixed by cleaning.',
    safe: [
      ['House Standard (Dawn + distilled)', 'Always safe.'],
      ['Vinegar 1:1', 'Safe for outer surfaces. Avoid pooling near the seal.'],
      ['Windex Original', 'Fine.'],
      ['Razor scrape', 'Safe at proper angle.'],
    ],
    avoid: [
      ['Anything that pools near the spacer bar', 'Solutions wicking into the seal cause spacer corrosion.'],
      ['Magic Eraser, abrasive pads', 'Standard glass scratching risk.'],
    ],
    notes:
      'If you see condensation, fog, or visible deposits between the two panes, the seal has failed. No cleaning method will help — the unit needs to be replaced. Continue cleaning the inside and outside surfaces normally; the inner failure is independent of your work.',
  },
  'low-e': {
    name: 'Low-E Coated',
    italic: 'most modern, most sensitive',
    rating: 'HIGH sensitivity',
    summary:
      'A microscopically thin metallic film bonded to one of the glass surfaces (usually the interior face of the outer pane), engineered to reflect infrared radiation while passing visible light. Almost universal in homes built or renovated since 2010. The coating gives the glass a faint silver, blue, or green cast — easy to miss but always present. The coating is irreversibly damaged by acid.',
    safe: [
      ['House Standard (Dawn + distilled)', 'pH-neutral and explicitly approved by every coating manufacturer.'],
      ['Manufacturer-approved glass cleaners', 'Cardinal IG, Pilkington, and Saint-Gobain all publish approved-cleaner lists.'],
      ['Razor scrape, low angle', 'Safe IF used carefully. The coating is on the outer pane interior face, so outside cleaning does not touch it.'],
    ],
    avoid: [
      ['Vinegar in any form', 'Acid degrades the coating bonds. Damage is cumulative and permanent.'],
      ['Ammonia (Windex Original, etc.)', 'The blue formula is mostly safe, but ammonia-based "professional" cleaners damage the coating. Read labels.'],
      ['Citrus-based cleaners', 'Same acidity problem as vinegar.'],
      ['CLR / mineral removers', 'Stronger acids than vinegar. Will strip coating in a single use.'],
      ['Abrasive cloths or pads', 'Coating is microscopically thin. Anything abrasive scratches through to the glass.'],
    ],
    notes:
      'Low-E damage is cumulative. A single application of vinegar may not show immediate effects, but the coating is irreversibly compromised in the area of contact. Repeated applications will eventually show visible damage — patchy areas where the coating has lifted, sometimes with a slight rainbow-like interference pattern at the edges of affected zones. There is no repair; the entire pane must be replaced.',
  },
  'factory-tinted': {
    name: 'Factory-Tinted Glass',
    italic: 'integrated tint, durable',
    rating: 'Medium sensitivity',
    summary:
      'Glass with the tint integrated into the glass itself during manufacture (gray, bronze, blue, or green). The tint is in the glass, not on it, so it cannot be scratched or stripped off. Most factory-tinted residential glass is also low-E coated, so treat it as low-E for cleaning safety.',
    safe: [
      ['House Standard (Dawn + distilled)', 'Always safe.'],
      ['Manufacturer-approved cleaners', 'Same approved list as low-E.'],
    ],
    avoid: [
      ['Acidic cleaners (vinegar, citrus, CLR)', 'Most factory-tinted glass also has low-E coating — same damage risk.'],
      ['Abrasive cloths or pads', 'Same risk as low-E.'],
    ],
    notes:
      'If you cannot determine whether your factory-tinted glass also has a low-E coating, assume it does. Most modern tinted residential glass is dual-purpose. The cleaning protocol for low-E covers both safely.',
  },
  'film-tinted': {
    name: 'Aftermarket Film-Tinted',
    italic: 'vulnerable, requires special care',
    rating: 'HIGH sensitivity',
    summary:
      'A polyester or polyurethane film applied to the interior surface of the glass — common on older homes where owners added aftermarket tint, and on most car windows. The film is on the *inside* face of the inside pane, so outside cleaning is normal, but interior cleaning must avoid anything that attacks the film.',
    safe: [
      ['House Standard (Dawn + distilled)', 'Safe on both sides.'],
      ['Soft microfiber cloth', 'Always.'],
    ],
    avoid: [
      ['Ammonia-based cleaners (Windex Original)', 'Ammonia degrades the film adhesive. Bubbling, peeling, and discoloration follow within months.'],
      ['Razor blade on interior surface', 'Will slice through the film. Outside surface only.'],
      ['Paper towels', 'Slightly abrasive against film; produces a hazy "scrubbed" look over time.'],
      ['Vinegar (interior side)', 'Mild acid weakens adhesive over months.'],
    ],
    notes:
      'Aftermarket tint film is the single most-damaged surface in residential cleaning. Most of the damage comes from people using Windex on the interior surface. The blue color of the bottle is doing some real harm in the world. Use the House Standard exclusively on tinted interiors, with a fresh microfiber cloth every time.',
  },
  'reflective': {
    name: 'Reflective / One-Way',
    italic: 'rare in residential, special protocols apply',
    rating: 'HIGH sensitivity',
    summary:
      'A heavily reflective coating, sometimes called "one-way glass" or solar-reflective glass. Common in commercial applications, less common in residential except on west-facing facades in hot climates. The reflective coating is typically a metallic film and is highly sensitive to abrasion and acid.',
    safe: [
      ['House Standard, very gentle', 'Light pressure. Soft microfiber.'],
      ['Manufacturer-approved cleaners only', 'These should have been provided at install. If not, contact the glass manufacturer.'],
    ],
    avoid: [
      ['Acidic cleaners — all of them', 'Vinegar, citrus, CLR — anything acidic damages the metallic film.'],
      ['Razor blade', 'Will strip the coating in a single pass. Never on reflective glass.'],
      ['Abrasive anything', 'Reflective coatings are thinner than low-E and even more delicate.'],
      ['High-pressure washing', 'Mechanical force lifts the film over time.'],
    ],
    notes:
      'Reflective glass is sometimes installed without the homeowner knowing — it can be added during construction as part of an energy-code compliance package. If your west-facing windows look noticeably darker than your other windows from outside, you may have it. When in doubt, treat as the most sensitive coating and use only the House Standard.',
  },
  'laminated': {
    name: 'Laminated Impact Glass',
    italic: 'two layers + plastic interlayer',
    rating: 'Medium sensitivity',
    summary:
      'Two layers of glass bonded with a polyvinyl butyral (PVB) interlayer. Common in coastal hurricane zones, ground-floor windows in security-conscious areas, skylights, and some commercial storefronts. The exterior glass surface cleans normally, but damage to the edge seal compromises the laminate bond.',
    safe: [
      ['House Standard (Dawn + distilled)', 'Always safe on glass surfaces.'],
      ['Vinegar 1:1', 'Safe on glass surfaces. Avoid edges.'],
      ['Razor blade', 'Safe at proper angle on the glass face. Do not scrape near the edge.'],
    ],
    avoid: [
      ['Solution pooling at the edge', 'Water wicking into the laminate edge causes delamination — visible as a creeping cloudy haze inward from the perimeter.'],
      ['Pressure washing the edge', 'Same delamination risk, accelerated.'],
      ['Heat (steam cleaners)', 'Heat softens the PVB interlayer; do not use heat on laminated glass.'],
    ],
    notes:
      'The most common laminated-glass failure is edge delamination, where moisture wicks into the layer between the two panes. It looks like a gradual cloudy fog spreading inward from the frame. Once it starts, it cannot be stopped — keep solutions away from the edges entirely on laminated glass.',
  },
};

// ─── component ────────────────────────────────────────────────────────────
export default function TintIdentifier() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [dark, setDark] = useState(false);

  const isComplete = step >= QUESTIONS.length;
  const verdict = isComplete ? VERDICTS[diagnose(answers)] : null;

  function answer(qId, optId) {
    setAnswers({ ...answers, [qId]: optId });
    setStep(step + 1);
  }

  function back() {
    if (step > 0) {
      setStep(step - 1);
    }
  }

  function reset() {
    setStep(0);
    setAnswers({});
  }

  const T = dark
    ? {
        bg: '#1A1D1C', surface: '#22262A', ink: '#F4F1EA',
        inkSoft: '#B8B2A4', inkFaint: '#6B6960', rule: '#2E3236',
        sienna: '#D67148', sage: '#9CA89A',
      }
    : {
        bg: '#F4F1EA', surface: '#FAF7F1', ink: '#1A1D1C',
        inkSoft: '#5A5A52', inkFaint: '#A39E91', rule: '#D9D3C5',
        sienna: '#B5532A', sage: '#9CA89A',
      };

  const fontDisplay = "'Fraunces', 'Times New Roman', serif";
  const fontBody = "'Inter Tight', 'Helvetica Neue', sans-serif";
  const fontMono = "'JetBrains Mono', 'Courier New', monospace";

  const currentQ = !isComplete ? QUESTIONS[step] : null;
  const progress = isComplete ? 1 : step / QUESTIONS.length;

  return (
    <div
      style={{
        minHeight: '100vh', background: T.bg, color: T.ink,
        fontFamily: fontBody,
        transition: 'background 0.3s, color 0.3s',
        padding: '32px 20px 80px',
      }}
    >
      <StaticSeo schemaType="webApplication" path="/tools/tint-identifier" />
      <link
        rel="stylesheet"
        href="https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,300;9..144,400;9..144,500;9..144,600&family=Inter+Tight:wght@300;400;500;600&family=JetBrains+Mono:wght@400;500&display=swap"
      />

      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(12px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes slideIn {
          from { opacity: 0; transform: translateX(8px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        .reveal { animation: fadeUp 0.6s cubic-bezier(.2,.7,.3,1) both; }
        .reveal-1 { animation-delay: 0.05s; }
        .reveal-2 { animation-delay: 0.15s; }
        .reveal-3 { animation-delay: 0.25s; }
        .question-enter { animation: fadeUp 0.5s cubic-bezier(.2,.7,.3,1) both; }
        .option-tile { animation: slideIn 0.4s cubic-bezier(.2,.7,.3,1) both; }
        .option-tile:nth-child(1) { animation-delay: 0.05s; }
        .option-tile:nth-child(2) { animation-delay: 0.10s; }
        .option-tile:nth-child(3) { animation-delay: 0.15s; }
        .option-tile:nth-child(4) { animation-delay: 0.20s; }
        .option-tile:hover {
          background: ${T.surface};
          border-left-color: ${T.sienna} !important;
          transform: translateX(2px);
        }
        .option-tile { transition: background 0.2s, border-color 0.2s, transform 0.2s; }
        .ghost-btn:hover { color: ${T.sienna}; }
        .pill-btn:hover { background: ${T.ink}; color: ${T.bg}; }

        @media (max-width: 720px) {
          .h1-title { font-size: 36px !important; line-height: 1.05 !important; }
          .q-title { font-size: 28px !important; }
        }
      `}</style>

      <div style={{ maxWidth: 880, margin: '0 auto' }}>
        {/* Header */}
        <header
          style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
            paddingBottom: 20, borderBottom: `1px solid ${T.rule}`, marginBottom: 56,
          }}
        >
          <div style={{ fontFamily: fontDisplay, fontSize: 22, letterSpacing: '-0.01em' }}>
            Window Washing <em style={{ fontStyle: 'italic', fontWeight: 400 }}>Guide</em>
          </div>
          <button
            onClick={() => setDark(!dark)}
            style={{
              background: 'transparent',
              border: `1px solid ${T.rule}`,
              borderRadius: '50%',
              width: 32, height: 32, color: T.inkSoft,
              cursor: 'pointer', fontSize: 13,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            {dark ? '☀' : '☾'}
          </button>
        </header>

        {/* Title */}
        <div className="reveal reveal-1">
          <div
            style={{
              fontFamily: fontMono, fontSize: 11,
              letterSpacing: '0.18em', color: T.sienna,
              marginBottom: 20,
            }}
          >
            § THE TOOLS &nbsp;·&nbsp; 06 / 09 &nbsp;·&nbsp; DAMAGE-PREVENTION
          </div>

          <h1
            className="h1-title"
            style={{
              fontFamily: fontDisplay, fontSize: 56, fontWeight: 400,
              lineHeight: 1.04, letterSpacing: '-0.025em',
              margin: '0 0 24px', maxWidth: 800,
            }}
          >
            Tint & Coating{' '}
            <em style={{ fontStyle: 'italic', color: T.inkSoft, fontWeight: 400 }}>
              Identifier
            </em>
            .
          </h1>

          <p
            style={{
              fontFamily: fontDisplay, fontSize: 21, fontWeight: 300,
              lineHeight: 1.5, color: T.inkSoft,
              maxWidth: 620, margin: '0 0 12px',
            }}
          >
            Six questions, one verdict. Identify your glass before you ruin it
            — and find out which cleaners are safe and which will void the warranty.
          </p>

          <p
            style={{
              fontFamily: fontMono, fontSize: 11,
              letterSpacing: '0.05em', color: T.inkFaint, marginBottom: 56,
            }}
          >
            Verdicts cite manufacturer care guidelines (Cardinal IG, Pilkington, Saint-Gobain).
          </p>
        </div>

        {/* Progress */}
        {!isComplete && (
          <div className="reveal reveal-2" style={{ marginBottom: 40 }}>
            <div
              style={{
                display: 'flex', justifyContent: 'space-between',
                fontFamily: fontMono, fontSize: 10,
                letterSpacing: '0.18em', color: T.inkFaint,
                marginBottom: 10,
              }}
            >
              <span>QUESTION {step + 1} OF {QUESTIONS.length}</span>
              <span>{Math.round(progress * 100)}% COMPLETE</span>
            </div>
            <div style={{ height: 1, background: T.rule, position: 'relative' }}>
              <div
                style={{
                  position: 'absolute', top: -1, left: 0,
                  height: 3, background: T.sienna,
                  width: `${progress * 100}%`,
                  transition: 'width 0.4s cubic-bezier(.2,.7,.3,1)',
                }}
              />
            </div>
          </div>
        )}

        {/* Active question */}
        {!isComplete && currentQ && (
          <div className="question-enter" key={currentQ.id}>
            <h2
              className="q-title"
              style={{
                fontFamily: fontDisplay, fontSize: 38, fontWeight: 400,
                lineHeight: 1.1, letterSpacing: '-0.025em',
                margin: '0 0 6px',
              }}
            >
              {currentQ.title}
            </h2>
            <div
              style={{
                fontFamily: fontDisplay, fontStyle: 'italic',
                fontSize: 18, color: T.sienna,
                marginBottom: 20,
              }}
            >
              {currentQ.italic}
            </div>
            <p
              style={{
                fontFamily: fontDisplay, fontSize: 16, fontWeight: 300,
                lineHeight: 1.55, color: T.inkSoft,
                maxWidth: 640, margin: '0 0 32px',
              }}
            >
              {currentQ.note}
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {currentQ.options.map((opt, i) => (
                <button
                  key={opt.id}
                  className="option-tile"
                  onClick={() => answer(currentQ.id, opt.id)}
                  style={{
                    background: 'transparent',
                    border: `1px solid ${T.rule}`,
                    borderLeft: `2px solid transparent`,
                    padding: '20px 24px',
                    cursor: 'pointer',
                    fontFamily: fontBody,
                    color: T.ink, textAlign: 'left',
                    display: 'grid',
                    gridTemplateColumns: '32px 1fr auto',
                    gap: 16, alignItems: 'center',
                  }}
                >
                  <div
                    style={{
                      fontFamily: fontMono, fontSize: 10,
                      letterSpacing: '0.15em', color: T.inkFaint,
                    }}
                  >
                    № 0{i + 1}
                  </div>
                  <div>
                    <div style={{ fontFamily: fontDisplay, fontSize: 19, marginBottom: 4 }}>
                      {opt.label}
                    </div>
                    <div style={{ fontSize: 13, color: T.inkSoft, lineHeight: 1.4 }}>
                      {opt.desc}
                    </div>
                  </div>
                  <div
                    style={{
                      fontFamily: fontMono, fontSize: 16,
                      color: T.inkFaint,
                    }}
                  >
                    →
                  </div>
                </button>
              ))}
            </div>

            {step > 0 && (
              <button
                onClick={back}
                className="ghost-btn"
                style={{
                  marginTop: 32,
                  background: 'transparent', border: 'none',
                  color: T.inkSoft,
                  fontFamily: fontMono, fontSize: 11,
                  letterSpacing: '0.18em', cursor: 'pointer',
                  padding: '8px 0',
                }}
              >
                ← BACK TO QUESTION {step}
              </button>
            )}
          </div>
        )}

        {/* Verdict */}
        {isComplete && verdict && (
          <div>
            <div className="reveal reveal-1">
              <div
                style={{
                  fontFamily: fontMono, fontSize: 11,
                  letterSpacing: '0.18em', color: T.sienna,
                  marginBottom: 16,
                }}
              >
                ◆ DIAGNOSIS &nbsp;·&nbsp; {verdict.rating.toUpperCase()}
              </div>

              <h2
                style={{
                  fontFamily: fontDisplay, fontSize: 48, fontWeight: 400,
                  lineHeight: 1.05, letterSpacing: '-0.025em',
                  margin: '0 0 6px',
                }}
              >
                {verdict.name}
              </h2>
              <div
                style={{
                  fontFamily: fontDisplay, fontStyle: 'italic',
                  fontSize: 22, color: T.sienna,
                  marginBottom: 24,
                }}
              >
                {verdict.italic}
              </div>

              <p
                style={{
                  fontFamily: fontDisplay, fontSize: 18, fontWeight: 300,
                  lineHeight: 1.6, color: T.inkSoft,
                  maxWidth: 640, margin: '0 0 40px',
                }}
              >
                {verdict.summary}
              </p>
            </div>

            {/* Safe + Avoid panels */}
            <div
              className="reveal reveal-2"
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
                gap: 24, marginBottom: 32,
              }}
            >
              {/* SAFE */}
              <div
                style={{
                  background: T.surface,
                  borderLeft: `2px solid ${T.sage}`,
                  padding: '28px 28px',
                }}
              >
                <div
                  style={{
                    fontFamily: fontMono, fontSize: 11,
                    letterSpacing: '0.18em', color: T.sage,
                    marginBottom: 16,
                  }}
                >
                  ✓ SAFE TO USE
                </div>
                {verdict.safe.map(([name, note], i) => (
                  <div
                    key={i}
                    style={{
                      padding: '12px 0',
                      borderTop: i === 0 ? 'none' : `1px solid ${T.rule}`,
                    }}
                  >
                    <div
                      style={{
                        fontFamily: fontDisplay, fontSize: 16,
                        color: T.ink, marginBottom: 4,
                      }}
                    >
                      {name}
                    </div>
                    <div
                      style={{
                        fontFamily: fontBody, fontSize: 13,
                        color: T.inkSoft, lineHeight: 1.45,
                      }}
                    >
                      {note}
                    </div>
                  </div>
                ))}
              </div>

              {/* AVOID */}
              <div
                style={{
                  background: T.surface,
                  borderLeft: `2px solid ${T.sienna}`,
                  padding: '28px 28px',
                }}
              >
                <div
                  style={{
                    fontFamily: fontMono, fontSize: 11,
                    letterSpacing: '0.18em', color: T.sienna,
                    marginBottom: 16,
                  }}
                >
                  ✕ AVOID — WILL DAMAGE
                </div>
                {verdict.avoid.map(([name, note], i) => (
                  <div
                    key={i}
                    style={{
                      padding: '12px 0',
                      borderTop: i === 0 ? 'none' : `1px solid ${T.rule}`,
                    }}
                  >
                    <div
                      style={{
                        fontFamily: fontDisplay, fontSize: 16,
                        color: T.ink, marginBottom: 4,
                      }}
                    >
                      {name}
                    </div>
                    <div
                      style={{
                        fontFamily: fontBody, fontSize: 13,
                        color: T.inkSoft, lineHeight: 1.45,
                      }}
                    >
                      {note}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Notes panel */}
            <div
              className="reveal reveal-3"
              style={{
                background: T.bg,
                border: `1px solid ${T.rule}`,
                padding: '24px 28px',
                marginBottom: 40,
              }}
            >
              <div
                style={{
                  fontFamily: fontMono, fontSize: 11,
                  letterSpacing: '0.18em', color: T.sienna,
                  marginBottom: 12,
                }}
              >
                ⚡ DIAGNOSTIC NOTES
              </div>
              <p
                style={{
                  fontFamily: fontDisplay, fontSize: 16, fontWeight: 300,
                  lineHeight: 1.65, color: T.ink, margin: 0,
                  fontStyle: 'italic',
                }}
              >
                {verdict.notes}
              </p>
            </div>

            {/* Actions */}
            <div
              className="reveal reveal-3"
              style={{
                display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 40,
              }}
            >
              <button
                onClick={reset}
                className="pill-btn"
                style={{
                  background: 'transparent',
                  border: `1px solid ${T.ink}`,
                  color: T.ink,
                  fontFamily: fontMono, fontSize: 11,
                  letterSpacing: '0.18em',
                  padding: '14px 28px', cursor: 'pointer',
                  transition: 'background 0.2s, color 0.2s',
                }}
              >
                ↺ TEST ANOTHER WINDOW
              </button>
              <button
                onClick={() => window.print()}
                className="pill-btn"
                style={{
                  background: 'transparent',
                  border: `1px solid ${T.ink}`,
                  color: T.ink,
                  fontFamily: fontMono, fontSize: 11,
                  letterSpacing: '0.18em',
                  padding: '14px 28px', cursor: 'pointer',
                  transition: 'background 0.2s, color 0.2s',
                }}
              >
                PRINT VERDICT →
              </button>
            </div>

            {/* Cross-links */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                gap: 0,
                borderTop: `1px solid ${T.rule}`,
                marginBottom: 40,
              }}
            >
              {[
                ['DIAGNOSTICIAN', 'The vinegar question — when it works, when it ruins your glass', '/articles/vinegar-question-when-it-works'],
                ['THE TOOLS', 'The Solution Calculator — pick a safe recipe →', '/tools/solution-calculator'],
                ['TECHNIQUE LIBRARY', 'The Razor Scrape — angles for coated glass', '/tools/technique-library'],
              ].map(([kicker, title, to]) => (
                <Link
                  key={title}
                  to={to}
                  className="ghost-btn"
                  style={{
                    padding: '20px 0',
                    borderBottom: `1px solid ${T.rule}`,
                    textDecoration: 'none', color: T.ink,
                  }}
                >
                  <div
                    style={{
                      fontFamily: fontMono, fontSize: 10,
                      letterSpacing: '0.18em', color: T.sienna,
                      marginBottom: 8,
                    }}
                  >
                    {kicker}
                  </div>
                  <div
                    style={{
                      fontFamily: fontDisplay, fontSize: 17,
                      lineHeight: 1.3, paddingRight: 16,
                    }}
                  >
                    {title}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Footer */}
        <footer
          style={{
            marginTop: 40, paddingTop: 24,
            borderTop: `1px solid ${T.rule}`,
            display: 'flex', justifyContent: 'space-between',
            alignItems: 'baseline', flexWrap: 'wrap', gap: 16,
            fontFamily: fontMono, fontSize: 11,
            color: T.inkFaint, letterSpacing: '0.1em',
          }}
        >
          <span>NO. 042 &nbsp;·&nbsp; TOOL 06 OF 09</span>
          <Link
            to="/tools/tint-identifier/guide"
            style={{
              color: T.sienna,
              textDecoration: 'none',
              fontFamily: fontMono, fontSize: 11,
              letterSpacing: '0.1em',
            }}
          >
            READ THE METHODOLOGY →
          </Link>
        </footer>
      </div>
    </div>
  );
}
